import * as THREE from "three";
import { heightMapToNormalMap } from "./proceduralNormalMap";

// Lightweight cache so we don't regenerate identical textures repeatedly
const foamTextureCache = new Map<string, THREE.CanvasTexture>();

function fbmNoise(x: number, y: number, octaves: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  const maxOctaves = Math.min(octaves, 4);

  for (let i = 0; i < maxOctaves; i++) {
    const n =
      Math.sin(x * 12.9898 * frequency + y * 78.233 * frequency) * 43758.5453;
    const noise = n - Math.floor(n);
    value += amplitude * noise;
    frequency *= 2;
    amplitude *= 0.5;
  }

  return value;
}

function generateFoamHeightMap(
  size: number,
  scale: number,
  cellularFactor: number,
): Float32Array {
  const data = new Float32Array(size * size);
  const step = Math.max(1, Math.floor(size / 128));

  for (let y = 0; y < size; y += step) {
    for (let x = 0; x < size; x += step) {
      const u = (x / size) * scale;
      const v = (y / size) * scale;

      // Base soft noise
      const base = fbmNoise(u, v, 3);

      // Subtle cellular modulation (for small pores)
      const cellX = Math.sin(u * Math.PI * 2) * 0.5 + 0.5;
      const cellY = Math.cos(v * Math.PI * 2) * 0.5 + 0.5;
      const cells = (cellX * cellY) ** 2;

      const height = base * (1 - cellularFactor) + cells * cellularFactor;

      for (let dy = 0; dy < step && y + dy < size; dy++) {
        for (let dx = 0; dx < step && x + dx < size; dx++) {
          data[(y + dy) * size + (x + dx)] = height;
        }
      }
    }
  }

  return data;
}

function generateNoppenHeightMap(size: number, waves: number): Float32Array {
  const data = new Float32Array(size * size);
  const freq = waves * Math.PI * 2;

  for (let y = 0; y < size; y++) {
    const v = y / size;
    for (let x = 0; x < size; x++) {
      const u = x / size;

      // Gleichmäßiges Wellenmuster: „Eierschachtel“ aus Sinus-Wellen
      const sx = Math.sin(u * freq);
      const sy = Math.sin(v * freq);
      const height = 0.5 + 0.5 * sx * sy;

      data[y * size + x] = height;
    }
  }

  return data;
}

function getFoamNormalMap(
  size: number,
  scale: number,
  intensity: number,
  cellularFactor: number,
): THREE.CanvasTexture {
  const key = `${size}-${scale}-${intensity}-${cellularFactor}`;
  const cached = foamTextureCache.get(key);
  if (cached) {
    return cached.clone();
  }

  const heightMap = generateFoamHeightMap(size, scale, cellularFactor);
  const normalMap = heightMapToNormalMap(size, heightMap, intensity);

  if (foamTextureCache.size > 16) {
    const firstKey = foamTextureCache.keys().next().value;
    if (firstKey) {
      foamTextureCache.delete(firstKey);
    }
  }
  foamTextureCache.set(key, normalMap);

  return normalMap.clone();
}

function getNoppenNormalMap(
  size: number,
  waves: number,
  intensity: number,
): THREE.CanvasTexture {
  const key = `noppen-${size}-${waves}-${intensity}`;
  const cached = foamTextureCache.get(key);
  if (cached) {
    return cached.clone();
  }

  const heightMap = generateNoppenHeightMap(size, waves);
  const normalMap = heightMapToNormalMap(size, heightMap, intensity);

  if (foamTextureCache.size > 16) {
    const firstKey = foamTextureCache.keys().next().value;
    if (firstKey) {
      foamTextureCache.delete(firstKey);
    }
  }
  foamTextureCache.set(key, normalMap);

  return normalMap.clone();
}

function getSlottedNormalMap(
  size: number,
  slots: number,
  intensity: number,
): THREE.CanvasTexture {
  const key = `slot-${size}-${slots}-${intensity}`;
  const cached = foamTextureCache.get(key);
  if (cached) {
    return cached.clone();
  }

  const heightMap = new Float32Array(size * size);
  const freq = slots * Math.PI * 2;

  for (let y = 0; y < size; y++) {
    const v = y / size;
    // Geschnittene Struktur: tiefe, parallele Rillen
    const stripe = Math.sin(v * freq);
    const height = 0.5 + 0.5 * stripe; // -1..1 → 0..1
    for (let x = 0; x < size; x++) {
      heightMap[y * size + x] = height;
    }
  }

  const normalMap = heightMapToNormalMap(size, heightMap, intensity);

  if (foamTextureCache.size > 16) {
    const firstKey = foamTextureCache.keys().next().value;
    if (firstKey) {
      foamTextureCache.delete(firstKey);
    }
  }
  foamTextureCache.set(key, normalMap);

  return normalMap.clone();
}

function applyPanelSizeRepeat(
  map: THREE.CanvasTexture,
  panelSize?: THREE.Vector2,
): void {
  if (!panelSize) return;
  const repeatX = panelSize.x / 300;
  const repeatY = panelSize.y / 300;
  map.repeat.set(repeatX, repeatY);
}

/** Standard/Default foam: dunkelgrau, feine zufällige Poren. */
export const getStandardFoamMaterial = (
  panelSize?: THREE.Vector2,
): THREE.MeshStandardMaterial => {
  const size = 256;
  const normalMap = getFoamNormalMap(size, 10, 0.9, 0.2);
  applyPanelSizeRepeat(normalMap, panelSize);

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color("#333333"),
    roughness: 0.9,
    metalness: 0,
    normalMap,
    normalScale: new THREE.Vector2(1, 1),
  });
};

/** Weicher Foam: hellblau, gleiche Porenstruktur. */
export const getSoftFoamMaterial = (
  panelSize?: THREE.Vector2,
): THREE.MeshStandardMaterial => {
  const size = 256;
  const normalMap = getFoamNormalMap(size, 10, 0.9, 0.2);
  applyPanelSizeRepeat(normalMap, panelSize);

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color("#aaccff"),
    roughness: 0.9,
    metalness: 0,
    normalMap,
    normalScale: new THREE.Vector2(1, 1),
  });
};

/** Noppenschaum: Eierschachtelstruktur. */
export const getEggboxFoamMaterial = (
  panelSize?: THREE.Vector2,
): THREE.MeshStandardMaterial => {
  const size = 256;

  const normalMap = getNoppenNormalMap(size, 8, 2.2);
  applyPanelSizeRepeat(normalMap, panelSize);

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color("#4a4a4a"),
    roughness: 0.9,
    metalness: 0,
    normalMap,
    normalScale: new THREE.Vector2(1.7, 1.7),
  });
};

/** Geschlitzter Foam: parallele Rillen. */
export const getSlottedFoamMaterial = (
  panelSize?: THREE.Vector2,
): THREE.MeshStandardMaterial => {
  const size = 256;

  const normalMap = getSlottedNormalMap(size, 20, 2);
  applyPanelSizeRepeat(normalMap, panelSize);

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color("#3a3a3a"),
    roughness: 0.9,
    metalness: 0,
    normalMap,
    normalScale: new THREE.Vector2(1.4, 1.4),
  });
};
